"use client";

/**
 * Monatsraster (Musterkatalog M-084 „Intranet lesen“, Design-Guide `monats-raster.md`): die Karte „November 2026“ mit
 * Vor-/Zurück-Knöpfen, Wochentagskopf (Montag zuerst) und den Tagen; Tage mit Terminen tragen einen Punkt, heute ist
 * gefüllt. Reine Ansicht: kein Tag ist anklickbar, die Termine des Monats stehen daneben in der Liste. Herkunft:
 * `mini-month-calendar-card-with-event-dots`, auf Kartengröße (`size-9`) aufgeweitet.
 *
 * Kontrolliert: Aufrufer berechnet Titel und Wochen (`monatsWochen`) und hält den Monat; der Baustein lädt nichts und prüft
 * keine Rechte, `markiert` enthält nur Tage mit Terminen, die die Person lesen darf (r110).
 *
 * Abweichung AW-100 (2): auf Kartengröße aufgeweitet (`size-9`, Sieben-Spalten-Raster), Tage nicht anklickbar
 * (siehe `docs/ui-preline/abweichungen.json`).
 */
const KOPF = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;

const NAV_KNOPF =
  "m-px size-7 flex justify-center items-center text-muted-foreground-1 hover:bg-surface-hover hover:text-foreground rounded-full disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-surface-focus";

// Punkt unter dem Tag: auf dem gefüllten „heute“-Kreis in der Vordergrundfarbe, sonst in der Primärfarbe.
const PUNKT = "absolute bottom-0.5 inset-s-1/2 size-1 bg-primary rounded-full -translate-x-1/2";
const PUNKT_HEUTE = "absolute bottom-0.5 inset-s-1/2 size-1 bg-primary-foreground rounded-full -translate-x-1/2";

export function MonatsRaster({
  titel,
  wochen,
  markiert,
  heute,
  onZurueck,
  onVor,
}: {
  /** „November 2026“. */
  titel: string;
  wochen: readonly (readonly (number | null)[])[];
  /** Tage (1-31) mit mindestens einem Termin. */
  markiert: ReadonlySet<number>;
  /** Heutiger Tag (1-31), wenn der angezeigte Monat der laufende ist; sonst `null`. */
  heute: number | null;
  onZurueck: () => void;
  onVor: () => void;
}) {
  return (
    <>
      {/* Preline: productivity/productivity-sub-calendars-scheduling/productivity-cat-month-and-year-calendars/mini-month-calendar-card-with-event-dots */}
      <section aria-label={`Monatsübersicht ${titel}`} className="bg-card border border-card-line shadow-2xs rounded-xl p-5">
        <div className="flex justify-between items-center gap-1 mb-3">
          <h2 className="ps-1 text-lg font-semibold text-foreground">{titel}</h2>
          <div className="flex justify-end items-center gap-1">
            <button type="button" className={NAV_KNOPF} aria-label="Vorheriger Monat" onClick={onZurueck}>
              <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <button type="button" className={NAV_KNOPF} aria-label="Nächster Monat" onClick={onVor}>
              <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>
        <div role="table" aria-label={titel}>
          <div role="row" className="grid grid-cols-7 mb-1.5">
            {KOPF.map((k) => (
              <span key={k} role="columnheader" className="block text-center text-xs uppercase text-muted-foreground-1">
                {k}
              </span>
            ))}
          </div>
          {wochen.map((woche, i) => (
            <div key={i} role="row" className="grid grid-cols-7">
              {woche.map((tag, j) =>
                tag === null ? (
                  <span key={j} role="cell" />
                ) : (
                  <span key={j} role="cell" className="flex justify-center">
                    <span
                      className={
                        tag === heute
                          ? "m-px relative size-9 flex justify-center items-center border border-primary-line bg-primary text-sm font-medium text-primary-foreground rounded-full"
                          : "m-px relative size-9 flex justify-center items-center text-sm text-foreground rounded-full"
                      }
                    >
                      {tag}
                      {markiert.has(tag) && (
                        <>
                          <span className={tag === heute ? PUNKT_HEUTE : PUNKT} aria-hidden="true" />
                          <span className="sr-only">, Termin</span>
                        </>
                      )}
                    </span>
                  </span>
                ),
              )}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
